import React, { useState } from 'react';
import type { TenantFilterOptions, TenantStatus } from '@/types';

interface TenantFiltersProps {
  onFiltersChange: (filters: TenantFilterOptions) => void;
  onClearFilters: () => void;
}

const TENANT_STATUSES: { value: TenantStatus; label: string }[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'TRIAL_REGISTERED', label: 'Registered' },
  { value: 'TRIAL_UNREGISTERED', label: 'Trial-unregistered' },
  { value: 'MIGRATION', label: 'Migration' },
  { value: 'ARCHIVED', label: 'Archived' },
  { value: 'PROVISION', label: 'Provisioning' },
  { value: 'ERROR', label: 'Error' },
  { value: 'SUBSCRIBED', label: 'Subscribed' },
];

export function TenantFilters({ onFiltersChange, onClearFilters }: TenantFiltersProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState<TenantFilterOptions>({});

  const handleFilterChange = (key: keyof TenantFilterOptions, value: any) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
  };

  const handleStatusChange = (value: string) => {
    if (value === '') {
      handleFilterChange('status', undefined);
    } else {
      handleFilterChange('status', value as TenantStatus);
    }
  };

  const handleApplyFilters = () => {
    onFiltersChange(filters);
  };

  const handleClearFilters = () => {
    setFilters({});
    onClearFilters();
  };

  const hasActiveFilters = Object.values(filters).some(value => {
    return value !== undefined && value !== null && value !== '';
  });

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.name) count++;
    if (filters.status) count++;
    if (filters.minLicenses !== undefined || filters.maxLicenses !== undefined) count++;
    return count;
  };

  return (
    <>
      {/* Filter Card */}
      <div className="filter-card">
        {/* Filter Header */}
        <div 
          className={`filter-header ${isExpanded ? 'expanded' : ''}`}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="filter-header-content">
            <h3 className="filter-title">Filter Tenants</h3>
            <p className="filter-description">Search and filter tenant data</p>
          </div>
          <button className="access-btn access-ghost">
            <span>{isExpanded ? 'Collapse' : 'Expand'}</span>
            <span className="text-caption">{isExpanded ? '▲' : '▼'}</span>
          </button>
        </div>

        {/* Filter Content */}
        <div className={`filter-content-wrapper ${isExpanded ? 'expanded' : 'collapsed'}`}>
          <div className="filter-content">
            <div className="access-form">
              {/* Tenant Name */}
              <div className="access-form-item">
                <label className="access-label" htmlFor="tenantName">
                  Tenant Name
                </label>
                <input
                  id="tenantName"
                  type="text"
                  placeholder="Enter tenant name..."
                  value={filters.name || ''}
                  onChange={(e) => handleFilterChange('name', e.target.value)}
                  className="access-input"
                />
              </div>

              {/* Status */}
              <div className="access-form-item">
                <label className="access-label" htmlFor="tenantStatus">
                  Status
                </label>
                <select
                  id="tenantStatus"
                  value={filters.status || ''}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className="access-select"
                >
                  <option value="">All Statuses</option>
                  {TENANT_STATUSES.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              {/* License Count Range */}
              <div className="access-form-item">
                <label className="access-label" htmlFor="minLicenses">
                  Number of Licenses
                </label>
                <div className="range-inputs">
                  <input
                    id="minLicenses"
                    type="number"
                    placeholder="Min"
                    min="0"
                    value={filters.minLicenses || ''}
                    onChange={(e) => handleFilterChange('minLicenses', e.target.value ? parseInt(e.target.value) : undefined)}
                    className="access-input"
                  />
                  <span className="range-separator">to</span>
                  <input
                    id="maxLicenses"
                    type="number"
                    placeholder="Max"
                    min="0"
                    value={filters.maxLicenses || ''}
                    onChange={(e) => handleFilterChange('maxLicenses', e.target.value ? parseInt(e.target.value) : undefined)}
                    className="access-input"
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="filter-actions">
              <button
                onClick={handleClearFilters}
                className="access-btn access-secondary"
              >
                Clear All Filters
              </button>
              <button
                onClick={handleApplyFilters}
                className="access-btn access-primary"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Active Filter Banner */}
      {hasActiveFilters && (
        <div className="alert alert-info">
          <span>
            {getActiveFiltersCount()} filter{getActiveFiltersCount() > 1 ? 's' : ''} applied
          </span>
          <button onClick={handleClearFilters}>
            Clear all filters
          </button>
        </div>
      )}
    </>
  );
}
